import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AskKnowledge } from "@/components/knowledge/AskKnowledge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, ImagePlus, PlayCircle, Upload } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  ["welcome", "Welcome to Top-Recruit"], ["retail", "The Retail Path"], ["acquisition", "Agency Acquisition"],
  ["associate", "Associate Agent"], ["protege", "Protégé Producer"],
];
const MEDIA = [["hero_url", "Hero image"], ["hero_caption", "Hero caption"], ["video_url", "Video link (https)"],
  ["video_thumb_url", "Video thumbnail"], ["signature_url", "Signature image"], ["book_cover_url", "Book cover image"]];

function embedUrl(u) {
  const y = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (y) return `https://www.youtube-nocookie.com/embed/${y[1]}`;
  const v = u.match(/vimeo\.com\/(\d+)/);
  return v ? `https://player.vimeo.com/video/${v[1]}` : null;
}

function Video({ m }) {
  if (!m.video_url) {
    return (
      <div className="pw-video-empty" data-testid="pw-video-placeholder">
        {m.video_thumb_url ? <img src={m.video_thumb_url} alt="" /> : <PlayCircle className="w-10 h-10 opacity-70" />}
        <p className="font-semibold">A Welcome from Jeff Hastings</p><p className="text-sm opacity-80">Video coming soon.</p>
      </div>);
  }
  const e = embedUrl(m.video_url);
  return e
    ? <iframe title="Welcome video" src={e} className="pw-video" allow="fullscreen; picture-in-picture" sandbox="allow-scripts allow-same-origin allow-presentation" data-testid="pw-video" />
    : <video className="pw-video" src={m.video_url} poster={m.video_thumb_url || undefined} controls data-testid="pw-video" />;
}

function MediaEditor({ page, onClose, onSaved }) {
  const [m, setM] = useState({ ...(page.media || {}) });
  const [subtitle, setSubtitle] = useState(page.subtitle || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef({});
  const upload = async (k, f) => {
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/help/admin/images", fd); setM((x) => ({ ...x, [k]: data.url })); toast.success("Image uploaded"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/pages/admin/${page.id}`, { media: m, subtitle }); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pw-media-dialog">
        <DialogHeader><DialogTitle>Page media — {page.title}</DialogTitle></DialogHeader>
        <label className="text-xs font-medium text-slate-600">Subtitle<Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} data-testid="pw-media-subtitle" /></label>
        {MEDIA.map(([k, l]) => (
          <div key={k} className="flex items-end gap-2">
            <label className="flex-1 text-xs font-medium text-slate-600">{l}
              <Input value={m[k] || ""} onChange={(e) => setM({ ...m, [k]: e.target.value })} placeholder={k === "hero_caption" ? "" : "https://… or upload"} data-testid={`pw-media-${k}`} /></label>
            {!["hero_caption", "video_url"].includes(k) && <>
              <input type="file" accept="image/*" hidden ref={(el) => { fileRef.current[k] = el; }} onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current[k]?.click()} data-testid={`pw-media-upload-${k}`}><Upload className="w-3.5 h-3.5" /></Button>
            </>}
          </div>))}
        <p className="text-[11px] text-slate-500">Video: paste a YouTube, Vimeo or https MP4 link. The signature is a displayed graphic only, not an electronic signature.</p>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy} onClick={save} data-testid="pw-media-save">Save</Button></div>
      </DialogContent>
    </Dialog>);
}

export default function PathwaysToOwnership() {
  const { isSuper, isReferralPartner } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some(([k]) => k === sp.get("tab")) ? sp.get("tab") : "welcome";
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(false);
  const [editMedia, setEditMedia] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setPage(null); setErr(false);
    api.get(`/pages/view/pathways-${tab}`, { params: isSuper ? { preview: 1 } : {} }).then((r) => setPage(r.data)).catch(() => setErr(true));
  }, [tab, isSuper, tick]);
  useEffect(() => {
    document.querySelector("[data-testid='pw-tabs'] .pw-tab.is-active")?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [tab]);
  const go = (k) => { setSp({ tab: k }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const onBodyClick = (e) => {
    const a = e.target.closest("a"); if (!a) return;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("#tab-")) { e.preventDefault(); go(href.slice(5)); }
    else if (href === "#refer") { e.preventDefault(); navigate(isReferralPartner ? "/referrals/add" : "/referrals/invite"); }
    else if (/^https?:\/\//.test(href)) { a.setAttribute("target", "_blank"); a.setAttribute("rel", "noopener noreferrer"); }
  };
  const m = page?.media || {};
  return (
    <div className="pw-root -mx-4 sm:-mx-6 lg:-mx-8 -mt-4" data-testid="pathways-page">
      <div className="pw-tabs" role="tablist" aria-label="Pathways to Ownership" data-testid="pw-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={`pw-tab ${tab === k ? "is-active" : ""}`} onClick={() => go(k)} data-testid={`pw-tab-${k}`}>{l}</button>))}
      </div>
      {err && <p className="p-10 text-center text-slate-500" data-testid="pw-unavailable">This chapter isn't available yet.</p>}
      {page && (
        <div className="pw-page tr-animate-in">
          <header className="pw-hero" style={m.hero_url ? { backgroundImage: `linear-gradient(90deg, rgba(10,37,64,.88), rgba(10,37,64,.35)), url(${m.hero_url})` } : undefined} data-testid="pw-hero">
            <p className="pw-hero-kicker">Pathways to Ownership</p>
            <h1 data-testid="pw-title">{page.title}</h1>
            {page.subtitle && <p className="pw-hero-sub" data-testid="pw-subtitle">{page.subtitle}</p>}
            {m.hero_caption && <p className="pw-hero-cap">{m.hero_caption}</p>}
          </header>
          {isSuper && (
            <div className="pw-admin" data-testid="pw-admin-bar">
              <span>{page.status === "published" ? "Published" : `Status: ${page.status}`} · Master Admin</span>
              <Link to={`/admin/pages/${page.id}`} className="pw-admin-link" data-testid="pw-edit-page"><Pencil className="w-3.5 h-3.5" />Edit Page</Link>
              <button type="button" className="pw-admin-link" onClick={() => setEditMedia(true)} data-testid="pw-edit-media"><ImagePlus className="w-3.5 h-3.5" />{tab === "welcome" ? "Video, Signature & Images" : "Hero & Images"}</button>
            </div>)}
          <div className="pw-content">
            {tab === "welcome" && <div className="pw-video-wrap"><Video m={m} /></div>}
            <div className="tr-page pw-body" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: page.body || "" }} data-testid="pw-body" />
            {tab === "welcome" && (
              <div className="pw-signature" data-testid="pw-signature">
                {m.signature_url && <img src={m.signature_url} alt="Signature" />}
                <p className="font-semibold text-slate-900">Jeff Hastings</p><p className="text-sm text-slate-600">Founder &amp; CEO, Top-Recruit</p>
                {m.book_cover_url && <img src={m.book_cover_url} alt="So You Want to Be an Insurance Agent" className="pw-book" />}
              </div>)}
            {tab !== "welcome" && (
              <div className="pw-ask" data-testid="pw-resources">
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Resources — ask about current requirements</h3>
                <p className="text-sm text-slate-600 mb-3">Answers come only from Knowledge Center materials you're authorized to see.</p>
                <AskKnowledge compact />
              </div>)}
          </div>
        </div>)}
      {editMedia && page && <MediaEditor page={page} onClose={() => setEditMedia(false)} onSaved={() => { setEditMedia(false); setTick((t) => t + 1); }} />}
    </div>);
}
